/**
 * useFilterState Hook
 *
 * Manages filter state with immediate URL synchronization and localStorage
 * persistence scoped to one collection. Filters apply instantly when changed and
 * persist across page refreshes; a collection's records page and its reports
 * share one store, and no other collection sees it.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FilterConfig, DateRangeFilterConfig, UseFilterStateReturn, UseFilterStateOptions, ConductDimension, FilterLogic } from '../types/filter.types';
import {
  FILTER_PARAM_KEYS,
  RETIRED_FILTER_PARAM_KEYS,
  FILTER_STORAGE_KEY_PREFIX,
  filterStorageKey,
  VALID_DATE_RANGE_PRESETS,
  VALID_DIMENSIONS,
  VALID_FILTER_LOGIC,
  DEFAULT_FILTER_LOGIC,
} from '../constants/filter.constants';

// =============================================================================
// Storage Functions
// =============================================================================

/**
 * Save a collection's filters to localStorage.
 *
 * An empty scope stores nothing: a key with an empty suffix would be a global
 * store under a new name, which is the thing per-collection scoping removes.
 */
function saveFiltersToStorage(scope: string, filters: FilterConfig): void {
  if (!scope) return;
  try {
    if (Object.keys(filters).length === 0) {
      localStorage.removeItem(filterStorageKey(scope));
    } else {
      localStorage.setItem(filterStorageKey(scope), JSON.stringify(filters));
    }
  } catch {
    // Ignore storage errors (e.g., quota exceeded, private browsing)
  }
}

/**
 * Load a collection's filters from localStorage. An empty scope reads nothing.
 */
function loadFiltersFromStorage(scope: string): FilterConfig {
  if (!scope) return {};
  try {
    const stored = localStorage.getItem(filterStorageKey(scope));
    if (stored) {
      return sanitizeStoredFilters(JSON.parse(stored) as FilterConfig);
    }
  } catch {
    // Ignore parse errors
  }
  return {};
}

/**
 * Remove the bare pre-scoping key.
 *
 * Its value is never read into a scope: that value is the state users found
 * confusing — one collection's filters showing on another — and no collection
 * can claim it. Runs on every initialisation rather than behind a module-level
 * "done" flag: a flag survives for the life of a Jest test file, so only the
 * first test to mount the hook would observe the retirement.
 */
function retireLegacyFilterStorage(): void {
  try {
    localStorage.removeItem(FILTER_STORAGE_KEY_PREFIX);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Drop a legacy/unknown date preset from persisted state so it can never reach
 * the API (a 400) or render as a broken "Date: undefined" chip. A stale preset
 * like `all_time` (no longer a valid value) with no explicit dates collapses to
 * an empty range — "all time / no filter"; if custom dates are present those are
 * kept and only the bad preset is stripped.
 */
function sanitizeStoredFilters(config: FilterConfig): FilterConfig {
  const cleaned: FilterConfig & { businessContext?: unknown; regulatoryFlags?: unknown } = {
    ...config,
  };
  delete cleaned.businessContext;
  delete cleaned.regulatoryFlags;

  const dr = cleaned.dateRange;
  if (!dr?.preset || (VALID_DATE_RANGE_PRESETS as readonly string[]).includes(dr.preset)) {
    return cleaned;
  }
  const { preset: _legacyPreset, ...rest } = dr;
  return { ...cleaned, dateRange: rest };
}

/**
 * Clear every collection's persisted filters from localStorage.
 *
 * Called when a session ends, so it must leave nothing behind: every scoped key
 * and the bare pre-scoping key, which the same prefix predicate matches. The
 * walk runs backwards because removing during a forward walk re-indexes the
 * remaining keys and skips one.
 */
export function clearFilterStorage(): void {
  try {
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (key === FILTER_STORAGE_KEY_PREFIX || key?.startsWith(`${FILTER_STORAGE_KEY_PREFIX}:`)) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignore storage errors
  }
}

// =============================================================================
// URL Serialization
// =============================================================================

/**
 * Convert FilterConfig to URL search params.
 * Only includes logic params when they differ from defaults.
 */
function filterConfigToParams(config: FilterConfig): URLSearchParams {
  const params = new URLSearchParams();

  const presetIsKnown =
    !!config.dateRange?.preset &&
    (VALID_DATE_RANGE_PRESETS as readonly string[]).includes(config.dateRange.preset);
  if (presetIsKnown) {
    params.set('dateRangePreset', config.dateRange!.preset!);
  } else if (
    config.dateRange &&
    !config.dateRange.from &&
    !config.dateRange.to
  ) {
    // Either an explicitly-cleared range OR an unknown/legacy preset with no
    // explicit dates (e.g. a stale `all_time`) — both mean "all time / no
    // filter". Persist the cleared sentinel so it survives a URL round-trip and
    // an unknown preset self-heals out of the URL instead of sticking.
    params.set('dateRangeCleared', '1');
  }
  if (config.dateRange?.from) {
    params.set('dateFrom', config.dateRange.from);
  }
  if (config.dateRange?.to) {
    params.set('dateTo', config.dateRange.to);
  }

  // Dimensions with logic
  if (config.dimensions?.values?.length) {
    params.set('dimensions', config.dimensions.values.join(','));
    if (config.dimensions.logic !== DEFAULT_FILTER_LOGIC.dimensions) {
      params.set('dimensionsLogic', config.dimensions.logic);
    }
  }


  if (config.search) {
    params.set('search', config.search);
  }
  if (config.conductScoreMin !== undefined) {
    params.set('conductScoreMin', String(config.conductScoreMin));
  }
  if (config.conductScoreMax !== undefined) {
    params.set('conductScoreMax', String(config.conductScoreMax));
  }

  return params;
}

/**
 * Build a link into the records queue for a collection, pre-applying a filter set.
 *
 * Reuses `filterConfigToParams` so a drill-down link and the records page's own
 * URL parsing share one grammar and cannot drift. Pass only the fields you want
 * carried (e.g. a single dimension plus the source report's date range); every
 * other filter is simply omitted.
 *
 * `queue` selects the landing queue tab (`?queue=`). Pass `'all'` for a filter
 * drill-down: the page otherwise defaults to the complaints queue, which layers
 * its own regulatory flag on top of your filter and hides matching records that
 * are not eligible complaints. Kept a plain string so the filter module does not
 * depend on the records component's QueueType; the records page validates it.
 */
export function buildRecordsHref(
  collectionId: string,
  config: FilterConfig,
  queue?: string,
): string {
  const params = filterConfigToParams(config);
  if (queue) {
    params.set('queue', queue);
  }
  const qs = params.toString();
  return `/v4/records/${collectionId}/records${qs ? `?${qs}` : ''}`;
}

/**
 * Validate and filter dimension values from URL params.
 */
function validateDimensions(values: string[]): ConductDimension[] {
  return values.filter((v): v is ConductDimension =>
    (VALID_DIMENSIONS as readonly string[]).includes(v)
  );
}

/**
 * Validate date range preset value from URL params.
 */
function validateDateRangePreset(value: string | null): DateRangeFilterConfig['preset'] {
  if (value && (VALID_DATE_RANGE_PRESETS as readonly string[]).includes(value)) {
    return value as DateRangeFilterConfig['preset'];
  }
  return undefined;
}

/**
 * Validate filter logic value from URL params.
 * Returns the default value if the provided value is invalid.
 */
function validateFilterLogic(value: string | null, defaultValue: FilterLogic): FilterLogic {
  if (value && (VALID_FILTER_LOGIC as readonly string[]).includes(value)) {
    return value as FilterLogic;
  }
  return defaultValue;
}

/**
 * Parse FilterConfig from URL search params with validation.
 */
function paramsToFilterConfig(params: URLSearchParams): FilterConfig {
  const config: FilterConfig = {};

  const dateRangePreset = params.get('dateRangePreset');
  const dateFrom = params.get('dateFrom');
  const dateTo = params.get('dateTo');
  const dateRangeCleared = params.get('dateRangeCleared');

  const validatedPreset = validateDateRangePreset(dateRangePreset);
  if (validatedPreset || dateFrom || dateTo) {
    config.dateRange = {
      preset: validatedPreset,
      from: dateFrom || undefined,
      to: dateTo || undefined,
    };
  } else if (dateRangeCleared === '1' || dateRangePreset) {
    // Explicitly-cleared range, OR a present-but-invalid preset with no dates
    // (e.g. a stale `all_time` deep link) — both mean "all time / no filter".
    // An empty object degrades identically to the localStorage path, rather than
    // falling through to `undefined` (which reports treat as the 30-day default).
    config.dateRange = {};
  }

  // Dimensions with logic
  const dimensions = params.get('dimensions');
  if (dimensions) {
    const validatedDimensions = validateDimensions(dimensions.split(',').filter(Boolean));
    if (validatedDimensions.length > 0) {
      config.dimensions = {
        values: validatedDimensions,
        logic: validateFilterLogic(params.get('dimensionsLogic'), DEFAULT_FILTER_LOGIC.dimensions),
      };
    }
  }


  const search = params.get('search');
  if (search) {
    config.search = search;
  }

  const conductScoreMin = params.get('conductScoreMin');
  if (conductScoreMin) {
    const parsed = parseInt(conductScoreMin, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
      config.conductScoreMin = parsed;
    }
  }

  const conductScoreMax = params.get('conductScoreMax');
  if (conductScoreMax) {
    const parsed = parseInt(conductScoreMax, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
      config.conductScoreMax = parsed;
    }
  }

  return config;
}

/**
 * Check if URL has any filter params.
 */
function hasFilterParams(params: URLSearchParams): boolean {
  return (FILTER_PARAM_KEYS as readonly string[]).some((key) => params.has(key));
}

/**
 * Check if a URL parameter key is filter-related.
 */
function isFilterParam(key: string): boolean {
  return (
    (FILTER_PARAM_KEYS as readonly string[]).includes(key) ||
    (RETIRED_FILTER_PARAM_KEYS as readonly string[]).includes(key)
  );
}

/**
 * Derive the filters a scope opens with: live URL filter params win, then that
 * collection's stored state, then empty.
 *
 * Run once per scope — at mount and again on every scope change — which is also
 * what retires the legacy bare key repeatedly and deliberately without a
 * module-level flag (see `retireLegacyFilterStorage`).
 */
function deriveFiltersForScope(scope: string, params: URLSearchParams): FilterConfig {
  if (scope) {
    retireLegacyFilterStorage();
  }
  // URL params take priority, for deep links and sharing.
  return hasFilterParams(params) ? paramsToFilterConfig(params) : loadFiltersFromStorage(scope);
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * Hook to manage filter state with URL synchronization and per-collection
 * localStorage persistence.
 *
 * Filters apply immediately when changed, URL params take priority over stored
 * filters so a deep link wins, and non-filter params (`page`, `pageSize`, a
 * report's own drill-down) are preserved across a rewrite.
 *
 * @param scope - the collection id whose filters these are. A collection's
 * records page and its reports pass the same id and so share one store. An
 * empty scope (the route param has not resolved) touches storage not at all and
 * behaves as URL-only.
 *
 * @param options - configuration for callers that own URL params of their own.
 * @param options.clearParamsOnChange - non-filter URL params the caller owns
 * that a filter change invalidates. They are dropped in this hook's rewrite
 * because react-router hands every updater in a flush the same pre-navigation
 * snapshot, so a caller clearing its own param in a second `setSearchParams`
 * would overwrite the new filters. A filter key listed here is re-set by the
 * same rewrite and so cannot erase live state.
 *
 * @example
 * ```tsx
 * const { filters, setFilters, reset } = useFilterState(collectionId || '');
 *
 * return (
 *   <FilterStrip
 *     filters={filters}
 *     onFiltersChange={setFilters}
 *     onReset={reset}
 *   />
 * );
 * ```
 */
export function useFilterState(
  scope: string,
  options?: UseFilterStateOptions,
): UseFilterStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Held in a ref rather than a dependency: this is configuration, not reactive
  // data, and a caller passing an inline array would otherwise give the save
  // effect a new identity on every render.
  const clearParamsOnChangeRef = useRef(options?.clearParamsOnChange);
  clearParamsOnChangeRef.current = options?.clearParamsOnChange;

  const [filters, setFiltersState] = useState<FilterConfig>(() =>
    deriveFiltersForScope(scope, searchParams)
  );

  // The scope these filters were derived for. React Router reuses the mounted
  // element when only a path parameter changes, so one instance can be shown a
  // second collection.
  const [initialisedScope, setInitialisedScope] = useState(scope);

  // Re-derive during render, not in an effect: an effect would paint one frame
  // of the previous collection's filters and — because the save effect runs on
  // every change — write them under the new collection's key before the
  // correction arrived. This is React's documented pattern for adjusting state
  // when a prop changes; the guard is what prevents a loop, and only this
  // component's own state is set.
  if (scope !== initialisedScope) {
    setInitialisedScope(scope);
    setFiltersState(deriveFiltersForScope(scope, searchParams));
  }

  // Seeded empty so the save effect below treats the first run for a scope as a
  // mount: it fires when the derived state is non-empty, and is silent when {}.
  const prevFiltersRef = useRef<FilterConfig>({});

  // The scope this effect last ran for, so a scope change can be treated as a
  // mount rather than compared against the previous collection's filters.
  const lastSyncedScopeRef = useRef(scope);

  // The first sync is a mount, not a filter change, so it clears nothing: a deep
  // link carrying both a filter and a caller-owned param keeps both.
  const isFirstSyncRef = useRef(true);

  // Update URL and this collection's stored filters when filters change
  useEffect(() => {
    // On a scope change the previous scope's filters are not a baseline: two
    // collections holding the same stored range would compare equal and leave
    // the URL without its filter params while the page fetched with them. Treat
    // the first run for any scope as a mount and compare against {}.
    const isFirstSync = isFirstSyncRef.current;
    isFirstSyncRef.current = false;
    const scopeChanged = lastSyncedScopeRef.current !== scope;
    const previousFilters = scopeChanged ? {} : prevFiltersRef.current;
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(previousFilters);
    lastSyncedScopeRef.current = scope;
    prevFiltersRef.current = filters;

    // Only sync to URL when filters actually changed — otherwise we overwrite
    // pagination params (page, pageSize) set by other components.
    if (!filtersChanged) return;

    // Save under this collection's key
    saveFiltersToStorage(scope, filters);

    // Update URL
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams();

        // Preserve non-filter params
        prev.forEach((value, key) => {
          if (!isFilterParam(key)) {
            next.set(key, value);
          }
        });

        // Drop the caller-owned params a filter change invalidates. Ordered
        // before the filter params are set, so a filter key passed here is
        // re-added below rather than erasing state the user just chose.
        if (!isFirstSync) {
          clearParamsOnChangeRef.current?.forEach((key) => next.delete(key));
        }

        // Add filter params
        const filterParams = filterConfigToParams(filters);
        filterParams.forEach((value, key) => {
          next.set(key, value);
        });

        // Reset page to 1 when filters change
        if (next.has('page')) {
          next.set('page', '1');
        }

        return next;
      },
      { replace: true }
    );
  }, [scope, filters, setSearchParams]);

  // An empty scope has no key to watch, so it attaches no listener at all.
  const scopedKey = scope ? filterStorageKey(scope) : null;

  // Cross-tab synchronisation via storage event. The event fires in other
  // documents only, never in the one that made the change.
  useEffect(() => {
    if (!scopedKey) return;

    function handleStorageChange(event: StorageEvent) {
      // A null key means another tab called localStorage.clear(). Nothing in
      // the app does outside tests; this is completeness, not a live path.
      if (event.key === null) {
        setFiltersState({});
        return;
      }
      // Another collection's key — a tab on collection A must not react to
      // collection B's changes.
      if (event.key !== scopedKey) return;

      try {
        // A null newValue means the other tab removed the key: its own reset,
        // or the sign-out sweep. Either way this tab holds no filters.
        const newFilters: FilterConfig = event.newValue
          ? (JSON.parse(event.newValue) as FilterConfig)
          : {};
        // Sanitize like the initial load — a stale/unknown preset written by
        // another tab must not slip in and break the date chip / API call.
        setFiltersState(sanitizeStoredFilters(newFilters));
      } catch {
        // Ignore malformed JSON from other tabs
      }
    }

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [scopedKey]);

  // Set filters (applies immediately)
  const setFilters = useCallback((newFilters: FilterConfig) => {
    setFiltersState(newFilters);
  }, []);

  // Update a single field in filters
  const updateFilter = useCallback(<K extends keyof FilterConfig>(key: K, value: FilterConfig[K]) => {
    setFiltersState((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Reset all filters to empty
  const reset = useCallback(() => {
    setFiltersState({});
  }, []);

  return {
    filters,
    setFilters,
    updateFilter,
    reset,
  };
}
